import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, pw);
      toast.success("Welcome back");
      nav("/admin");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto mt-8">
        <div className="ks-card p-8">
          <h1 className="text-2xl font-semibold text-center" data-testid="admin-login-title">Sign In</h1>
          <p className="text-sm text-center text-[var(--ks-text-soft)] mt-2 mb-6">
            Don&apos;t have an account? <Link to="/" className="ks-link font-medium" data-testid="signup-link">Sign Up</Link>
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[13px] font-semibold">Username</Label>
              <Input
                id="email"
                data-testid="admin-email-input"
                placeholder="e.g. johndoe"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="pw" className="text-[13px] font-semibold">Password</Label>
                <button type="button" className="text-xs text-[var(--ks-primary)] hover:underline" data-testid="forgot-password">
                  Forgot your password?
                </button>
              </div>
              <div className="relative mt-1">
                <Input
                  id="pw"
                  type={showPw ? "text" : "password"}
                  data-testid="admin-password-input"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="**************"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ks-text-soft)] hover:text-[var(--ks-text)]"
                  data-testid="toggle-password-visibility"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="rounded border-[var(--ks-border)] w-4 h-4"
              />
              <span className="text-sm text-[var(--ks-text-soft)]">Remember &amp; Auto Login</span>
            </label>

            {/* Cloudflare-style verification badge (visual only) */}
            <div className="rounded-md bg-[#1f2937] text-white text-sm px-4 py-3 flex items-center justify-between" data-testid="cf-verify">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-400" />
                <span>Verified</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-slate-300">
                <span className="font-semibold text-[#f38020]">CLOUDFLARE</span>
                <span className="text-slate-400">Privacy · Terms</span>
              </div>
            </div>

            {err && <div className="text-sm text-[var(--ks-failed)]" data-testid="admin-login-error">{err}</div>}

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)] uppercase tracking-wide font-semibold"
              data-testid="admin-login-submit"
            >
              {busy ? "Signing in…" : "Login"}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
